import React from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Zap } from "lucide-react";

export default function AdminMasterPanel() {
  const [logs, setLogs] = useState([]);
  const [health, setHealth] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);

  const load = async () => {
    try {
      const [logsRes, healthRes] = await Promise.all([
        api.get("/admin/master-panel/logs"),
        api.get("/admin/master-panel/health"),
      ]);
      setLogs(logsRes.data);
      setHealth(healthRes.data);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const generateDummy = async () => {
    if (!window.confirm("Generate dummy data? Ini akan menambahkan user, order, likes, dan komentar palsu ke database.")) return;
    setGenerating(true);
    setGenResult(null);
    try {
      const res = await api.post("/admin/generate-dummy-data");
      setGenResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error("Gagal generate dummy data");
    } finally {
      setGenerating(false);
    }
  };

  const generateProducts = async () => {
    if (!window.confirm("Generate 1000+ produk? Ini akan menambahkan banyak produk ke katalog.")) return;
    setGenerating(true);
    try {
      const res = await api.post("/admin/generate-products");
      toast.success(res.data.message);
    } catch {
      toast.error("Gagal generate produk");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="admin-master-panel">
      <div className="label-mono text-zinc-500">// EXTERNAL SYSTEM</div>
      <h1 className="font-display text-3xl md:text-4xl font-black uppercase mt-1">Master Panel</h1>

      <div className="mt-2 border border-black bg-[#FFD600] p-3 font-mono text-xs">
        ⚠ MOCKED INTEGRATION – Endpoint master panel pihak ketiga belum dikonfigurasi. Ganti dengan API real saat tersedia.
      </div>

      {/* DUMMY DATA GENERATOR */}
      <div className="mt-6 border border-black bg-white p-5">
        <div className="label-mono text-zinc-500 mb-1">// DUMMY DATA GENERATOR</div>
        <h2 className="font-display text-xl font-black uppercase mt-1 mb-3">Generate Data Palsu</h2>
        <p className="font-mono text-xs text-zinc-600 mb-4">
          Otomatis membuat: 50 user palsu, 100 order, ratusan likes, dan komentar di semua produk. Cocok untuk demo toko.
        </p>

        <div className="flex flex-wrap gap-2">
          <button onClick={generateDummy} disabled={generating}
            className="btn-primary inline-flex items-center gap-2 bg-[#FF6A00] border-[#FF6A00]">
            <Zap className="w-4 h-4" />
            {generating ? "// GENERATING..." : "Generate Dummy Data Sekarang"}
          </button>

          <button onClick={generateProducts} disabled={generating}
            className="btn-primary inline-flex items-center gap-2">
            <Zap className="w-4 h-4" />
            {generating ? "// GENERATING..." : "Generate 1000+ Produk Sekarang"}
          </button>
        </div>

        {genResult && (
          <div className="mt-4 border border-black p-4 bg-zinc-50">
            <div className="label-mono text-[#00C853] mb-2">// BERHASIL!</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="border border-black bg-white p-3 text-center">
                <div className="font-display text-2xl font-black">{genResult.results?.users || 0}</div>
                <div className="label-mono text-[10px] text-zinc-500">USER BARU</div>
              </div>
              <div className="border border-black bg-white p-3 text-center">
                <div className="font-display text-2xl font-black">{genResult.results?.orders || 0}</div>
                <div className="label-mono text-[10px] text-zinc-500">ORDER</div>
              </div>
              <div className="border border-black bg-white p-3 text-center">
                <div className="font-display text-2xl font-black">{genResult.results?.likes || 0}</div>
                <div className="label-mono text-[10px] text-zinc-500">LIKES</div>
              </div>
              <div className="border border-black bg-white p-3 text-center">
                <div className="font-display text-2xl font-black">{genResult.results?.comments || 0}</div>
                <div className="label-mono text-[10px] text-zinc-500">KOMENTAR</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        {health && (
          <>
            <div className="bg-white border border-black p-5 shadow-brutal">
              <div className="label-mono text-zinc-500">// STATUS</div>
              <div className="font-display text-3xl font-black uppercase mt-2 text-[#00C853]">{health.status}</div>
              <div className="font-mono text-[10px] text-zinc-500 mt-1">latency {health.latency_ms}ms</div>
            </div>
            <div className="bg-white border border-black p-5 shadow-brutal">
              <div className="label-mono text-zinc-500">// UPTIME</div>
              <div className="font-display text-3xl font-black mt-2">{health.uptime}</div>
              <div className="font-mono text-[10px] text-zinc-500 mt-1">version {health.version}</div>
            </div>
            <div className="bg-black text-white border border-black p-5 shadow-brutal">
              <div className="label-mono text-yellow-400">// QUEUE</div>
              <div className="font-display text-3xl font-black mt-2">{health.queue_depth}</div>
              <div className="font-mono text-[10px] text-zinc-400 mt-1">last sync {new Date(health.last_sync).toLocaleTimeString("id-ID")}</div>
            </div>
          </>
        )}
      </div>

      <div className="mt-8 border border-black bg-white">
        <div className="p-4 border-b border-black flex items-center justify-between">
          <div className="label-mono">// DISPATCH LOGS</div>
          <span className="font-mono text-[10px] text-zinc-500">{logs.length} entries</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-black">
            <tr>
              <th className="p-3 text-left label-mono">ORDER</th>
              <th className="p-3 text-left label-mono hidden md:table-cell">REQUEST</th>
              <th className="p-3 text-left label-mono">RESPONSE</th>
              <th className="p-3 text-left label-mono">STATUS</th>
              <th className="p-3 text-left label-mono hidden md:table-cell">TIME</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id} className="border-b border-zinc-200">
                <td className="p-3 font-mono text-[11px]">{l.order_id.slice(0, 8)}…</td>
                <td className="p-3 hidden md:table-cell font-mono text-[10px]">{l.request.method} {l.request.endpoint}</td>
                <td className="p-3 font-mono text-[10px]"><b>{l.response.code}</b> {l.response.message}</td>
                <td className="p-3"><StatusBadge status={l.status} /></td>
                <td className="p-3 hidden md:table-cell font-mono text-[10px]">{new Date(l.created_at).toLocaleString("id-ID")}</td>
              </tr>
            ))}
            {logs.length === 0 && (
              <tr>
                <td colSpan={5} className="p-8 text-center font-mono text-xs text-zinc-500">
                  // no logs yet – buat order untuk memicu dispatch
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}